import React, { createContext, useState, useEffect, useContext, useCallback } from 'react';
import api from '../services/api';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);

  // Validate token on mount
  useEffect(() => {
    const token = localStorage.getItem('access_token');
    if (token) {
      api.get('/api/users/me')
        .then(userData => {
          setUser(userData);
          return api.get('/api/users/me/subscription').catch(() => null);
        })
        .then(sub => {
          setSubscription(sub);
        })
        .catch(() => {
          api.clearTokens();
        })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  // Read the same canonical profile used during session restoration before
  // publishing authenticated state. Login responses may omit newer role flags.
  const completeSignIn = useCallback(async data => {
    if (!data.access_token) throw new Error(data.detail || 'خطا در ورود');
    api.setTokens(data.access_token, data.refresh_token);
    let profile;
    try {
      profile = await api.get('/api/users/me');
      if (!profile || !profile.id) throw new Error('دریافت مشخصات حساب کامل نشد؛ دوباره وارد شوید');
    } catch (error) {
      api.clearTokens();
      setUser(null);
      setSubscription(null);
      throw error;
    }
    setUser(profile);
    try {
      setSubscription(await api.get('/api/users/me/subscription'));
    } catch (_) {
      setSubscription(null);
    }
    return { ...data, user: profile };
  }, []);

  const login = useCallback(async (email, password) => {
    const data = await api.auth.login({ email, password });
    return completeSignIn(data);
  }, [completeSignIn]);

  const register = useCallback(async (email, password, fullName, accountType = 'trader') => {
    const data = await api.auth.register({ email, password, full_name: fullName, account_type: accountType });
    return completeSignIn(data);
  }, [completeSignIn]);

  const logout = useCallback(() => {
    api.clearTokens();
    setUser(null);
    setSubscription(null);
  }, []);

  const refreshSubscription = useCallback(async () => {
    try {
      const sub = await api.get('/api/users/me/subscription');
      setSubscription(sub);
    } catch (e) {
      setSubscription(null);
    }
  }, []);

  const updateUser = useCallback((updatedUser) => {
    setUser(updatedUser);
  }, []);

  const value = {
    user,
    subscription,
    loading,
    isAuthenticated: !!user,
    login,
    register,
    logout,
    refreshSubscription,
    updateUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;
